import type { PoolShippingSettleSummary } from '../../../../modules/api';
import { AlertTriangle } from '../../../../modules/design-system';
import { useTermsUrl } from '../../../../modules/footerLinks';
import { formatCarrier } from '../formatCarrier';

export interface RefundNoticeSectionProps {
  /** Which copy to show: every card refunded vs a mix of shipped + refunded. */
  variant: Exclude<PoolShippingSettleSummary, null>;
  /** Titles of the cards that couldn't be shipped (refunded). */
  refundedTitles: string[];
  /** Carrier tracking for the shipped portion of a partial redemption. */
  trackingNumber?: string | null;
  companyName?: string | null;
}

const ACCENT = 'var(--co-ShippingItemStatus-refundedFg, #FFB020)';
const STRONG = 'rgba(255,255,255,0.92)';
const MUTED = 'rgba(255,255,255,0.62)';

function TermsLink() {
  // Operator's `type: 'terms'` footer link when configured, platform default
  // otherwise. With neither — keep the link's look but make it inert: no
  // href (a placeholder anchor isn't focusable) + pointer-events off, so a
  // click can't navigate the iframe anywhere.
  const termsUrl = useTermsUrl();
  const enabled = Boolean(termsUrl);
  return (
    <a
      {...(enabled
        ? {
            href: termsUrl,
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : { 'aria-disabled': true })}
      className={`underline underline-offset-2 ${enabled ? '' : 'pointer-events-none'}`}
      style={{ color: 'rgba(255,255,255,0.85)' }}
    >
      Terms of Service
    </a>
  );
}

/**
 * Top card on the Delivery Show page when the warehouse couldn't ship part or
 * all of a redemption. Two copy variants driven by the backend `settle_summary`:
 *   • refunded          → every pulled card was unavailable (V1).
 *   • partially_shipped → some cards shipped, some were refunded (V2).
 * Closes with the shared Support entry point.
 */
export function RefundNoticeSection({
  variant,
  refundedTitles,
  trackingNumber,
  companyName,
}: RefundNoticeSectionProps) {
  const count = refundedTitles.length;
  const itemWord = count === 1 ? 'item' : 'items';
  const names = refundedTitles.join(', ');
  const tracking = trackingNumber
    ? `${companyName ? `${formatCarrier(companyName)} · ` : ''}${trackingNumber}`
    : null;

  return (
    <section>
      <div className="flex items-center gap-2">
        <AlertTriangle size={22} strokeWidth={1.8} style={{ color: ACCENT }} />
        <h3
          className="text-[16px] font-bold leading-[22px] tracking-[-0.3px]"
          style={{ color: ACCENT }}
        >
          {variant === 'refunded'
            ? 'Redemption Cancellation Notice'
            : 'Partial Redemption Update'}
        </h3>
      </div>

      {variant === 'refunded' ? (
        <p className="mt-3 text-[14px] leading-[20px]" style={{ color: MUTED }}>
          We were unable to fulfill your redemption for{' '}
          <span style={{ color: STRONG, fontWeight: 600 }}>
            {names || 'your pulled item(s)'}
          </span>
          . Upon reviewing our inventory, it has been determined to be sold,
          lost, damaged, or otherwise unavailable. These situations are rare,
          and we exhausted all options before arriving here.
        </p>
      ) : (
        <>
          <p className="mt-3 text-[14px] leading-[20px]" style={{ color: MUTED }}>
            We were unable to fulfill{' '}
            <span style={{ color: STRONG, fontWeight: 600 }}>
              {count} {itemWord}
            </span>{' '}
            from your redemption. Upon reviewing our inventory, they have been
            determined to be sold, lost, damaged, or otherwise unavailable.
            These situations are rare, and we exhausted all options before
            arriving here.
          </p>
          <p
            className="mt-2 text-[14px] leading-[20px]"
            style={{ color: STRONG }}
          >
            The remaining redeemed items are unaffected and being shipped to
            you.
          </p>
          {tracking && (
            <div className="mt-3">
              <h4
                className="text-[13px] font-bold tracking-[-0.3px]"
                style={{ color: 'var(--co-MyPacksItemCard-titleColor, #FFFFFF)' }}
              >
                Tracking
              </h4>
              <p
                className="mt-1 text-[14px] font-semibold leading-[20px]"
                style={{ color: 'rgba(255,255,255,0.92)' }}
              >
                {tracking}
              </p>
            </div>
          )}
        </>
      )}

      <p className="mt-3 text-[14px] leading-[20px]" style={{ color: MUTED }}>
        {variant === 'refunded'
          ? 'As a result, your account has been credited as follows:'
          : 'For the unavailable item(s), your account has been credited as follows:'}
      </p>
      <ul
        className="mt-2 list-disc space-y-2 pl-5 text-[14px] leading-[20px]"
        style={{ color: MUTED }}
      >
        <li>
          {variant === 'refunded'
            ? 'All applicable Shipping & Handling fees have been refunded to your wallet.'
            : 'All applicable Shipping & Handling fees for the affected item(s) have been refunded to your wallet.'}
        </li>
        <li>
          {variant === 'refunded'
            ? 'An additional wallet credit has been issued in the amount of the greater of: (a) the Fair Market Value of the pulled item, or (b) the original pack price.'
            : 'A wallet credit has been issued for each unavailable item in the amount of the greater of: (a) the Fair Market Value of the pulled item, or (b) the original pack price.'}
        </li>
      </ul>

      <p className="mt-3 text-[14px] leading-[20px]" style={{ color: MUTED }}>
        For full details regarding our fulfillment policies, please refer to our{' '}
        <TermsLink />.
      </p>
      <p className="mt-2 text-[14px] leading-[20px]" style={{ color: MUTED }}>
        We sincerely apologize for the inconvenience and appreciate your
        understanding.
      </p>

    </section>
  );
}
